import React, { useState } from 'react';
import { DownloadIcon, PrinterIcon, SparklesIcon, CloseIcon } from './Icons';
import { sounds } from '../utils/audio';

export default function Printables() {
  const [activePrintable, setActivePrintable] = useState(null);

  const printables = [
    {
      id: 'coloring',
      title: 'Meadow Animal Friends Coloring Page',
      category: 'Art & Fine Motor',
      pages: '1 Page • PDF/Print',
      tag: 'Ages 2–6',
      icon: '🎨',
      previewArt: '/assets/images/animal-cow.png',
      desc: 'Big, bold outlines of Miss Kiki, Cow, Duck, and Rabbit having a picnic under the treehouse. Perfect for crayons and finger paints.',
      content: {
        title: 'Kiki & Kids — Meadow Friends Coloring Sheet',
        instructions: 'Color Miss Kiki and her meadow animal friends! What colors will you pick today?',
        items: ['Miss Kiki in the treehouse', 'Cow having a grass snack', 'Duck swimming in the pond', 'Rabbit hopping through flowers']
      }
    },
    {
      id: 'firefly',
      title: 'Counting Cove Firefly Tracing Sheet',
      category: 'Early Maths & Writing',
      pages: '2 Pages • PDF/Print',
      tag: 'Ages 3–6',
      icon: '✨',
      previewArt: '/assets/images/pet-pip.png',
      desc: 'Practice tracing numbers 1 to 10 with guided directional arrows and count the glowing jars of fireflies on the beach.',
      content: {
        title: 'Kiki & Kids — Firefly Number Tracing (1 to 10)',
        instructions: 'Trace each number along the dotted lines, then color in the matching number of glowing fireflies!',
        items: ['Number 1: 1 glowing jar', 'Number 2: 2 singing frogs', 'Number 3: 3 tree planks', 'Number 5: 5 golden stars']
      }
    },
    {
      id: 'shapes',
      title: 'Shape Shore Scavenger Hunt & Cutouts',
      category: 'Geometry & Sorting',
      pages: '1 Page • PDF/Print',
      tag: 'Ages 4–7',
      icon: '📐',
      previewArt: '/assets/images/buddy-dino.png',
      desc: 'Cut out circles, triangles, rectangles, and pentagons to build your own sailboat and rescue island animals!',
      content: {
        title: 'Kiki & Kids — Shape Shore Boat Builder',
        instructions: 'Carefully cut out each shape with adult supervision. Glue them onto the island sea to make your sailboat!',
        items: ['1 big triangle for the sail', '1 rectangle for the mast', '1 trapezoid for the hull', '3 small circles for portholes']
      }
    }
  ];

  const handlePrint = (printable) => {
    sounds.playSound('tap');
    setActivePrintable(printable);
  };

  const triggerBrowserPrint = () => {
    sounds.playSound('pop');
    window.print();
  };

  return (
    <section id="printables" className="py-16 lg:py-24 bg-white relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto space-y-3 mb-16">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-pink-100 text-pink-800 font-heading font-bold text-xs uppercase tracking-wider">
            <SparklesIcon className="w-4 h-4 text-pink-600" />
            <span>Free Family Printables</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-black text-slate-900 tracking-tight">
            Off-Screen Fun for Busy Hands
          </h2>
          <p className="text-slate-600 font-medium text-base sm:text-lg">
            Download and print our free coloring pages, number tracing guides, and shape crafts for quiet afternoons and road trips.
          </p>
        </div>

        {/* Printables Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {printables.map((item) => (
            <div
              key={item.id}
              className="rounded-3xl bg-slate-50 border-2 border-slate-200 p-6 flex flex-col justify-between hover:border-kiki-coral transition-all shadow-sm hover:shadow-lg group"
            >
              <div>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-3xl">{item.icon}</span>
                  <span className="px-2.5 py-1 bg-white rounded-full border border-slate-200 text-[11px] font-heading font-bold text-slate-600">
                    {item.tag}
                  </span>
                </div>
                <div className="w-full h-36 rounded-2xl bg-white border border-slate-200 p-4 mb-4 flex items-center justify-center group-hover:scale-105 transition-transform">
                  <img src={item.previewArt} alt={item.title} className="max-h-full object-contain" />
                </div>
                <span className="text-xs font-bold text-kiki-coral uppercase tracking-wider">
                  {item.category}
                </span>
                <h3 className="font-heading font-bold text-lg text-slate-900 mt-1 mb-2">
                  {item.title}
                </h3>
                <p className="text-xs text-slate-500 font-medium leading-relaxed mb-4">
                  {item.desc}
                </p>
              </div>

              <div className="pt-4 border-t border-slate-200 flex items-center justify-between">
                <span className="text-xs text-slate-400 font-bold">{item.pages}</span>
                <button
                  onClick={() => handlePrint(item)}
                  className="btn-bouncy px-4 py-2 bg-kiki-coral text-white rounded-xl font-heading text-xs font-bold shadow-sm flex items-center gap-1.5"
                >
                  <PrinterIcon className="w-3.5 h-3.5" />
                  <span>Print Sheet</span>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Printable View / Modal */}
        {activePrintable && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-pop">
            <div className="bg-white rounded-3xl max-w-2xl w-full p-6 sm:p-8 shadow-2xl border-4 border-kiki-coral space-y-6 max-h-[90vh] overflow-y-auto">
              
              <div className="flex items-center justify-between border-b pb-4">
                <div>
                  <span className="text-xs font-heading font-bold text-kiki-coral uppercase tracking-wider">Free Activity Sheet</span>
                  <h4 className="font-heading font-black text-xl text-slate-900">{activePrintable.content.title}</h4>
                </div>
                <button
                  onClick={() => { sounds.playSound('tap'); setActivePrintable(null); }}
                  className="p-1 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600"
                >
                  <CloseIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Printable sheet mock preview */}
              <div className="border-4 border-dashed border-slate-300 rounded-2xl p-6 bg-slate-50/50 space-y-4">
                <div className="flex items-center justify-between text-xs text-slate-400 font-bold pb-2 border-b">
                  <span>Name: ______________________</span>
                  <span>Date: _________</span>
                </div>
                <p className="text-xs sm:text-sm font-semibold text-slate-700 italic">
                  💡 {activePrintable.content.instructions}
                </p>
                <div className="grid grid-cols-2 gap-4 py-4">
                  {activePrintable.content.items.map((item, idx) => (
                    <div key={idx} className="h-28 rounded-xl border-2 border-slate-200 bg-white p-3 flex flex-col justify-between">
                      <span className="text-xs font-bold text-slate-700">Part {idx + 1}</span>
                      <span className="text-xs text-slate-500">{item}</span>
                    </div>
                  ))}
                </div>
                <div className="text-center text-[11px] text-slate-400">
                  kikiandkids.com • Miss Kiki Learning Series • 100% Free For Personal &amp; Educational Use
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  onClick={() => { sounds.playSound('tap'); setActivePrintable(null); }}
                  className="px-5 py-2.5 rounded-2xl border-2 border-slate-200 font-heading font-bold text-xs text-slate-600"
                >
                  Close
                </button>
                <button
                  onClick={triggerBrowserPrint}
                  className="btn-bouncy px-6 py-2.5 rounded-2xl bg-kiki-coral text-white font-heading font-bold text-sm shadow-md flex items-center gap-2"
                >
                  <PrinterIcon className="w-4 h-4 text-white" />
                  <span>Print Document Now</span>
                </button>
              </div>

            </div>
          </div>
        )}

      </div>
    </section>
  );
}
